/**
 * Closing the forecast loop (OD5-1, U2-1): after the commit the forecast box says, as a three-row table, the player's call beside the no-change plan's figure on the SAME basis the field was asked on
 * (margin and loans written off: the year's average, from the preview's window figure less its move against the no-change plan; Marked CET1: the year-end figure).
 * Numeric calls only (a band call has no point to compare); a basis the preview does not carry is left out, never replaced by another.
 */
import type { ForecastStatus } from "@bsim/contracts";
import type { KpiId, PreviewResult } from "@bsim/domain";
import { WHEN_PHRASE, formatKpi, formatPrintedChange } from "../../format";
import { labelOf } from "../../format/labels";
import { shown } from "../previewRows";
import { pausedBaselineLine } from "../planRows";

const BASES = [{ kpi: "nim", at: "window" }, { kpi: "ncoRate", at: "window" }, { kpi: "cet1Marked", at: "yearEnd" }] as const;

function noChangeOf(result: PreviewResult, kpi: KpiId, at: "window" | "yearEnd"): number | null {
  const level = shown(at === "window" ? result.kpis[kpi] : result.yearEnd?.[kpi]);
  const move = shown(at === "window" ? result.deltasVsAutopilot[kpi] : result.yearEndDeltasVsNoChange?.[kpi]);
  return level === null || move === null ? null : level.point - move.point;
}

/** One measure of the forecast box's call table: the player's call beside the no-change plan's figure, the basis both are read on (muted under the name). */
export interface CallRow { readonly kpi: KpiId; readonly label: string; readonly basis: string; readonly mine: string; readonly theirs: string; /** On the first row only: why the no-change plan moved (`noChangeMovedLine`), printed under the table. */ readonly moved?: string }
export const CLOSING_LINE = "The year will show whether your call or the no-change plan was closer.";
const basisOf = (at: "window" | "yearEnd"): string => WHEN_PHRASE[at === "window" ? "yearAverage" : "yearEnd"];
const rowOf = (kpi: KpiId, mine: string, theirs: string, at: "window" | "yearEnd"): CallRow => ({ kpi, label: labelOf(kpi), basis: basisOf(at), mine, theirs });

/** The call table as dashes: the room the box keeps for it before the forecast is in, so it never grows when the rows arrive. */
export const forecastCallPlaceholder = (): readonly CallRow[] => BASES.map(({ kpi, at }) => rowOf(kpi, "–", "–", at));

export function forecastCallLines(forecast: ForecastStatus | null, result: PreviewResult | null, me: { readonly buybackPaused?: boolean } = {}): readonly CallRow[] {
  const call = forecast?.state === "committed" ? forecast.forecast : null;
  if (call === null || call === undefined || call.format !== "numeric" || result === null) return [];
  const rows = BASES.flatMap(({ kpi, at }) => {
    const mine = call[kpi], theirs = noChangeOf(result, kpi, at);
    return mine === undefined || theirs === null ? [] : [rowOf(kpi, formatKpi(kpi, mine), formatKpi(kpi, theirs), at)];
  });
  const moved = noChangeMovedLine(forecast, result, me);
  return moved === "" ? rows : rows.map((r, i) => (i === 0 ? { ...r, moved } : r));
}

/**
 * Why the no-change plan sits where it does against the player's call and against last year (G4-12): one plain sentence for the first called measure whose printed gap to last year is not zero, else "".
 * `me` names the paused-buyback driver the page already knows (`previewWatch.buybackPaused`); without it the plan "follows the consensus forecast for the year".
 */
export function noChangeMovedLine(forecast: ForecastStatus | null, result: PreviewResult | null, me: { readonly buybackPaused?: boolean } = {}): string {
  const call = forecast?.state === "committed" ? forecast.forecast : null;
  if (call === null || call === undefined || call.format !== "numeric" || result === null) return "";
  for (const { kpi, at } of BASES) {
    const plan = noChangeOf(result, kpi, at), mine = call[kpi], last = forecast?.noChangeBaseline?.[kpi];
    if (plan === null || mine === undefined || last === undefined) continue;
    const vsLast = formatPrintedChange(kpi, plan - last);
    if (formatKpi(kpi, plan) === formatKpi(kpi, last)) continue;
    const head = `The no-change plan puts ${labelOf(kpi).replace(/^[A-Z](?![A-Z0-9])/, (c) => c.toLowerCase())}, ${basisOf(at)}, at ${formatKpi(kpi, plan)}: ${vsLast} against last year (${formatKpi(kpi, last)}) and ${formatPrintedChange(kpi, plan - mine)} against your call (${formatKpi(kpi, mine)}).`;
    return `${head} ${me.buybackPaused === true ? `${pausedBaselineLine(vsLast)}.` : "The plan follows the consensus forecast for the year."}`;
  }
  return "";
}
